import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { FiatDepositStepsData } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-data.model";
import { FiatDepositStateService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit.state.service";
import { FiatDepositParamNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-param-names.enum";
import { PaymentOptionsFacade } from "@modules/+new/payments/features/payment-options/domain/+state/payment-options.facade";
import { PaymentOptionsForFiatSummary } from "@modules/+new/payments/features/payment-options/domain/entities/payment-options-summary.model";
import { PaymentOption } from "@modules/+new/payments/features/payment-options/domain/entities/payment-option.model";
import { FiatDepositStepNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-names.enum";
import { FiatDepositStepValidationData } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-validation-data.model";
import { FiatDepositStepValidationStatus } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-validation-status.model";
import { WalletsFacade } from "@modules/+new/wallets/domain/+state/wallets.facade";
import { FiatWallet } from "@modules/+new/wallets/domain/entities/fiat-wallet.model";
import { FiatWalletSummary } from "@modules/+new/wallets/domain/entities/fiat-wallet-summary.model";
import { prepareParamsToValidate } from "@modules/+new/payments/features/fiat-deposit/domain/entities/deposit-validation-params";
import { GenericCollection } from "@modules/+new/payments/shared-payments/entities/generic-collection.model";
import { ValidationFunction } from "@modules/+new/payments/features/fiat-deposit/domain/entities/validation-function.type";
import { ProviderShortNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/provider-short-names.enum";
import { CryptoCoinWallet } from "@modules/+new/wallets/domain/entities/cryptocoin-wallet.model";

@Injectable({
	providedIn: "any"
})
export class FiatDepositValidationService {
	public constructor(
		private router: Router,
		private fiatDepositStateService: FiatDepositStateService,
		private paymentOptionsFacade: PaymentOptionsFacade,
		private walletsFacade: WalletsFacade
	) {}

	public isStateValid$(): Observable<FiatDepositStepValidationStatus> {
		return this.fiatDepositStateService.getStepsData$().pipe(
			take(1),
			switchMap((stepsData: FiatDepositStepsData) =>
				this.isStepStateValid$(FiatDepositStepNames.Summary, stepsData)
			)
		);
	}

	public isStepStateValid$(
		stepName: FiatDepositStepNames,
		stepsData: FiatDepositStepsData,
		fromBack?: boolean,
		fromCheckout?: boolean
	): Observable<FiatDepositStepValidationStatus> {
		if (!this.stepNameExists(stepName)) {
			return of({ isStateValid: false });
		}

		const data = this.prepareValidationData(stepName, stepsData, fromBack, fromCheckout);
		if (!data.validators || data.validators.length === 0) {
			return of({ isStateValid: true, stepData: data.stepData });
		}

		return combineLatest(data.validators).pipe(
			map((states: boolean[]) => {
				const isStateValid = states.every((state: boolean) => state);
				return { isStateValid, stepData: data.stepData };
			})
		);
	}

	private stepNameExists(stepName: unknown): boolean {
		return Object.values(FiatDepositStepNames).includes(stepName as FiatDepositStepNames);
	}

	// TODO think about immer.js?
	private prepareValidationData(
		stepName: FiatDepositStepNames,
		stepsData: FiatDepositStepsData,
		fromBack?: boolean,
		fromCheckout?: boolean
	): FiatDepositStepValidationData {
		const paramsToValidate = prepareParamsToValidate(stepName, fromBack, fromCheckout);
		const initialSummary: FiatDepositStepValidationData = {
			validators: [],
			stepData: {}
		};

		return paramsToValidate?.reduce(
			(acc: FiatDepositStepValidationData, param: FiatDepositParamNames) => ({
				validators: [...acc.validators, this.getValidatorsMap(stepsData)[param]()],
				stepData: { ...acc.stepData, [param]: stepsData?.[param] }
			}),
			initialSummary
		);
	}

	private getValidatorsMap(stepsData: FiatDepositStepsData): GenericCollection<ValidationFunction> {
		return {
			[FiatDepositParamNames.Wallet]: () => this.isWalletGuidValid$(stepsData?.walletGuid),
			[FiatDepositParamNames.FiatPid]: () => this.isFiatPidValid$(stepsData?.fiatPid),
			[FiatDepositParamNames.PaymentOption]: () =>
				this.isPaymentOptionValid$(stepsData?.paymentOptionId, stepsData?.walletGuid),
			[FiatDepositParamNames.DepositAmount]: () => this.isDepositAmountValid$(stepsData?.depositAmount),
			[FiatDepositParamNames.IncludingFees]: () => this.isIncludingFeesValid$(stepsData?.includingFees),
			[FiatDepositParamNames.BankProvider]: () => this.isBankProviderNameValid$(stepsData?.bankProviderName),
			[FiatDepositParamNames.ParentBank]: () => this.isParentBankValid$(stepsData),
			[FiatDepositParamNames.Bank]: () => this.isBankValid$(stepsData?.bankId),
			[FiatDepositParamNames.Transaction]: () => this.isTransactionIdValid$(stepsData?.transactionId),
			[FiatDepositParamNames.PaymentUrl]: () => this.isPaymentUrlValid$(stepsData?.paymentUrl),
			[FiatDepositParamNames.AssetSymbol]: () => this.isAssetSymbolValid$(stepsData?.assetSymbol),
			[FiatDepositParamNames.NetworkId]: () => this.isNetworkValid$(stepsData?.networkId)
		};
	}

	// TODO FiatWallet -> Wallet in general (fiat + crypto)
	private isWalletGuidValid$(walletGuid: string): Observable<boolean> {
		return combineLatest([this.walletsFacade.getFiatWallets$(), this.walletsFacade.getCryptoWallets$()]).pipe(
			map(
				([fiatWallets, cryptoWallets]) =>
					this.isFiatWalletGuidValid(walletGuid, fiatWallets) ||
					this.isCryptoWalletGuidValid(walletGuid, cryptoWallets)
			)
		);
	}

	private isFiatPidValid$(fiatPid: string): Observable<boolean> {
		return of(!!fiatPid);
	}

	private isFiatWalletGuidValid(walletGuid: string, wallets: FiatWallet[]): boolean {
		if (!walletGuid || !wallets || wallets.length === 0) {
			return false;
		}

		const walletGuids = wallets.map((wallet: FiatWallet) => wallet.guid);
		return this.containsAllowedValue(walletGuids, walletGuid);
	}

	private isCryptoWalletGuidValid(walletGuid: string, wallets: CryptoCoinWallet[]): boolean {
		if (!walletGuid || !wallets || wallets.length === 0) {
			return false;
		}

		const walletGuids = wallets.map((wallet: CryptoCoinWallet) => wallet.guid);
		return this.containsAllowedValue(walletGuids, walletGuid);
	}

	private isPaymentOptionValid$(optionId: number, walletGuid: string): Observable<boolean> {
		return this.walletsFacade.getFiatWalletsSummaryByWalletGuid$(walletGuid).pipe(
			switchMap((fiatWalletSummary: FiatWalletSummary) =>
				this.paymentOptionsFacade.getPaymentOptionsSummaryByFiat$(fiatWalletSummary.fiat.id)
			),
			map((summary: PaymentOptionsForFiatSummary) => this.isPaymentOptionValid(optionId, summary))
		);
	}

	private isPaymentOptionValid(optionId: number, summary: PaymentOptionsForFiatSummary): boolean {
		if (!optionId || !summary?.availableOptions || summary.availableOptions.length === 0) {
			return false;
		}

		const paymentOptionIds = summary.availableOptions.map((option: PaymentOption) => option.id);
		return this.containsAllowedValue(paymentOptionIds, optionId);
	}

	private isDepositAmountValid$(depositAmount: number): Observable<boolean> {
		return of(this.isDepositAmountValid(depositAmount));
	}

	private isDepositAmountValid(depositAmount: number): boolean {
		// TODO check amount range (data from user/limits)
		return !!depositAmount;
	}

	private isIncludingFeesValid$(includingFeesParam: boolean): Observable<boolean> {
		return of(typeof includingFeesParam === "boolean");
	}

	private isBankProviderNameValid$(bankProviderName: string): Observable<boolean> {
		return of(this.isBankProviderNameValid(bankProviderName));
	}

	private isBankProviderNameValid(bankProviderName: string): boolean {
		return Object.values(ProviderShortNames).includes(bankProviderName as ProviderShortNames);
	}

	private isBankValid$(bankId: string): Observable<boolean> {
		// TODO check if bankId exists??
		return of(!!bankId);
	}

	private isParentBankValid$(stepsData: FiatDepositStepsData): Observable<boolean> {
		return !this.shouldValidateParentBank(stepsData)
			? of(true)
			: of(this.isParentBankValid(stepsData.parentBankId));
	}

	private isParentBankValid(parentBankId: string): boolean {
		// TODO check if parentBankId exists??
		return !!parentBankId;
	}

	private isTransactionIdValid$(transactionId: string): Observable<boolean> {
		return of(this.isTransactionIdValid(transactionId));
	}

	private isTransactionIdValid(transactionId: string): boolean {
		// TODO check if transactionId exists??
		return !!transactionId;
	}

	private isPaymentUrlValid$(paymentUrl: string): Observable<boolean> {
		return of(this.isPaymentUrlValid(paymentUrl));
	}

	private isPaymentUrlValid(paymentUrl: string): boolean {
		// TODO check if paymentUrl exists??
		return !!paymentUrl;
	}

	private isAssetSymbolValid$(assetSymbol: string): Observable<boolean> {
		return of(this.isAssetSymbolValid(assetSymbol));
	}

	private isAssetSymbolValid(assetSymbol: string): boolean {
		return !!assetSymbol;
	}

	private isNetworkValid$(networkId: string): Observable<boolean> {
		return of(this.isNetworkValid(networkId));
	}

	private isNetworkValid(networkId: string): boolean {
		return !!networkId;
	}

	private containsAllowedValue(array: unknown[], item: unknown): boolean {
		return array.some((element) => element === item);
	}

	private shouldValidateParentBank(stepsData: FiatDepositStepsData): boolean {
		return !!stepsData?.parentBankId && !stepsData?.bankId;
	}
}
